import { useState } from 'react';
import { Loader2, Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { FormIdentity, FormOptions, FormTextKey, FormTexts, ReviewContact, TextSize, TextWeight } from '@/api/reviews';

/** The guest-facing review form. ONE renderer for both the live phone preview in
 *  settings and the public /leave_review/<uuid> page, so what the manager sees
 *  while typing is exactly what the guest gets.
 *
 *  Identity is not configurable beyond two presets (Alexey 16.09): Linza default
 *  and a Surf Coffee style taken from surfcoffee.ru — monochrome, serif display
 *  headings, black pill button. Only the texts and their sizes are. */

export const SIZE_PX: Record<TextSize, number> = { sm: 13, md: 16, lg: 20, xl: 27 };

export const DEFAULT_TEXTS: Record<FormTextKey, { text: string; size: TextSize; weight: TextWeight }> = {
  title: { text: '', size: 'xl', weight: 'bold' },
  address: { text: '', size: 'sm', weight: 'normal' },
  // Тексты по умолчанию — те, что владелец выставил на PitStop (17.09, AI36):
  // гость должен понимать, что это не оценка на картах, а совет управляющему.
  rating_label: { text: '', size: 'md', weight: 'normal' },
  prompt: { text: 'Что сделать лучше?', size: 'lg', weight: 'bold' },
  prompt_subtitle: { text: '', size: 'sm', weight: 'normal' },
  hint: {
    text: 'Это не оценка, как на картах, а приватная обратная связь управляющему. Будем рады вашему совету!',
    size: 'sm', weight: 'normal',
  },
};
export const DEFAULT_OPTIONS: FormOptions = {
  stars: true,
  contact: { enabled: false, text: 'Ваш контакт (необязательно)' },
};

export function textOf(texts: FormTexts, key: FormTextKey) {
  const t = texts[key];
  return {
    text: t?.text ?? DEFAULT_TEXTS[key].text,
    size: t?.size ?? DEFAULT_TEXTS[key].size,
    weight: t?.weight ?? DEFAULT_TEXTS[key].weight,
    // Автокапс включён по умолчанию: до появления выключателя капслок у Surf
    // был просто частью оформления (Алексей 18.09, AI63).
    caps: t?.caps ?? true,
  };
}
const weightClass = (w: TextWeight) => (w === 'bold' ? 'font-semibold' : 'font-normal');
/** Выключенный автокапс должен перебивать `uppercase` из оформления. */
const capsClass = (caps: boolean) => (caps ? '' : 'normal-case');

const IDENTITY = {
  // Оформление Linza — в стиле продукта: акцент primary, а не зелёная кнопка,
  // мягкие нейтральные тона (Алексей 16.09, п. 13); внизу подпись платформы со
  // ссылкой (п. 14).
  linza: {
    page: 'bg-slate-50 text-slate-900 font-sans',
    card: 'bg-white rounded-3xl shadow-[0_8px_30px_rgba(15,23,42,0.06)] border border-slate-200/70',
    title: 'tracking-tight',
    button: 'bg-primary text-primary-foreground hover:opacity-90 rounded-2xl shadow-sm',
    starOn: 'fill-amber-400 text-amber-400',
    starOff: 'text-slate-300',
    input: 'rounded-2xl border border-slate-200 bg-slate-50 focus:border-primary focus:bg-white',
    logo: '/brand/linza-robot.jpg',
    logoClass: 'size-16 rounded-full object-cover shadow-sm',
    muted: 'text-slate-500',
    footer: 'Сделано на платформе LinzaMetrics',
    footerHref: 'https://linzametrics.com',
  },
  surf: {
    page: 'bg-white text-black font-sans',
    card: 'bg-white rounded-none border-y border-black',
    title: 'font-serif uppercase tracking-[0.08em]',
    button: 'bg-black text-white hover:bg-black/85 rounded-full uppercase tracking-[0.12em] text-sm',
    starOn: 'fill-black text-black',
    starOff: 'text-black/25',
    input: 'rounded-none border border-black bg-white focus:border-black',
    logo: '/brand/surf-coffee.png',
    logoClass: 'h-14 w-auto sm:h-20',
    muted: 'text-black/60',
    footer: 'SURF COFFEE®',
    footerHref: '',
  },
} satisfies Record<FormIdentity, Record<string, string>>;

export interface ReviewFormViewProps {
  identity: FormIdentity;
  branchName: string;
  address: string;
  texts: FormTexts;
  options?: FormOptions;
  /** Preview never submits; the button only shows the thank-you state. */
  onSubmit?: (payload: { rating: number | null; text: string; website: string; contact?: ReviewContact }) => Promise<void>;
  className?: string;
}

export function ReviewFormView({ identity, branchName, address, texts, options = DEFAULT_OPTIONS, onSubmit, className }: ReviewFormViewProps) {
  const id = IDENTITY[identity] ?? IDENTITY.linza;
  const [rating, setRating] = useState<number | null>(null);
  const [text, setText] = useState('');
  const [website, setWebsite] = useState('');
  const [contact, setContact] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);

  const title = textOf(texts, 'title');
  const addr = textOf(texts, 'address');
  const ratingLabel = textOf(texts, 'rating_label');
  const prompt = textOf(texts, 'prompt');
  const promptSub = textOf(texts, 'prompt_subtitle');
  const hint = textOf(texts, 'hint');

  async function send() {
    if (rating === null && !text.trim()) {
      setError(options.stars ? 'Поставьте оценку или напишите пару слов' : 'Напишите пару слов');
      return;
    }
    setError(null);
    if (!onSubmit) { setState('done'); return; }
    setState('sending');
    try {
      const c = contact.trim();
      await onSubmit({ rating, text: text.trim(), website, ...(options.contact.enabled && c ? { contact: { text: c } satisfies ReviewContact } : {}) });
      setState('done');
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || 'Не получилось отправить. Попробуйте ещё раз.');
      setState('idle');
    }
  }

  return (
    <div className={cn('flex min-h-full flex-col items-center px-5 pb-8 pt-5 sm:pt-8', id.page, className)}>
      {/* Логотип строго по центру и в превью, и на телефоне (п. 11). */}
      <div className="mb-4 flex w-full justify-center"><img src={id.logo} alt="" className={cn('mx-auto', id.logoClass)} /></div>
      <div className={cn('w-full max-w-sm px-5 py-6', id.card)}>
        <h1 className={cn('text-center leading-tight', id.title, weightClass(title.weight), capsClass(title.caps))} style={{ fontSize: SIZE_PX[title.size] }}>
          {title.text || branchName}
        </h1>
        {address && (
          <p className={cn('mt-1 text-center', id.muted, weightClass(addr.weight))} style={{ fontSize: SIZE_PX[addr.size] }}>{address}</p>
        )}

        {state === 'done' ? (
          <div className="py-10 text-center">
            <p className={cn('leading-snug', id.title)} style={{ fontSize: SIZE_PX.lg }}>Спасибо!</p>
            <p className={cn('mt-2', id.muted)} style={{ fontSize: SIZE_PX.sm }}>Ваш отзыв получил управляющий.</p>
          </div>
        ) : (
          <>
            {options.stars && (
            <div className="mt-6 text-center">
              {ratingLabel.text && (
                <p className={cn(id.muted, weightClass(ratingLabel.weight))} style={{ fontSize: SIZE_PX[ratingLabel.size] }}>{ratingLabel.text}</p>
              )}
              <div className="mt-2 flex justify-center gap-1.5" role="radiogroup" aria-label={ratingLabel.text || 'Оценка'}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    role="radio"
                    aria-checked={rating === n}
                    aria-label={`${n}`}
                    onClick={() => setRating(rating === n ? null : n)}
                    className="p-1 transition-transform active:scale-90"
                  >
                    <Star className={cn('size-9', rating !== null && n <= rating ? id.starOn : id.starOff)} strokeWidth={1.5} />
                  </button>
                ))}
              </div>
            </div>
            )}
            <div className="mt-6">
              {prompt.text && (
                <p className={cn('leading-snug', id.title, weightClass(prompt.weight))} style={{ fontSize: SIZE_PX[prompt.size] }}>{prompt.text}</p>
              )}
              {promptSub.text && (
                <p className={cn('mt-1', id.muted, weightClass(promptSub.weight))} style={{ fontSize: SIZE_PX[promptSub.size] }}>{promptSub.text}</p>
              )}
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value.slice(0, 2000))}
                rows={5}
                className={cn('mt-3 w-full resize-none px-3 py-2.5 text-[16px] outline-none transition-colors', id.input)}
              />
              {hint.text && (
                <p className={cn('mt-1.5', id.muted, weightClass(hint.weight))} style={{ fontSize: SIZE_PX[hint.size] }}>{hint.text}</p>
              )}
              {options.contact.enabled && (
                // Одно необязательное поле в том же стиле, что и поле отзыва,
                // подпись — плейсхолдером (Алексей 17.09, AI16.7).
                <input
                  type="text"
                  inputMode="email"
                  autoComplete="email"
                  value={contact}
                  onChange={(e) => setContact(e.target.value.slice(0, 200))}
                  placeholder={options.contact.text}
                  aria-label={options.contact.text}
                  className={cn('mt-3 w-full px-3 py-2.5 text-[16px] outline-none transition-colors placeholder:text-current placeholder:opacity-45', id.input)}
                />
              )}
              {/* Honeypot: invisible to people, bots fill it and get a silent «ok». */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                className="absolute -left-[9999px] size-px opacity-0"
                aria-hidden
              />
            </div>

            {error && <p className="mt-3 text-center text-sm text-red-600">{error}</p>}
            <button
              type="button"
              onClick={() => void send()}
              disabled={state === 'sending'}
              className={cn('mt-5 flex h-12 w-full items-center justify-center gap-2 font-medium transition-colors disabled:opacity-60', id.button)}
            >
              {state === 'sending' && <Loader2 className="size-4 animate-spin" />}
              Отправить
            </button>
          </>
        )}
      </div>
      {id.footerHref ? (
        <a href={id.footerHref} target="_blank" rel="noreferrer" className={cn('mt-6 text-[11px] tracking-wider underline-offset-2 hover:underline', id.muted)}>{id.footer}</a>
      ) : (
        <p className={cn('mt-6 text-[11px] tracking-wider', id.muted)}>{id.footer}</p>
      )}
    </div>
  );
}
